import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BenefitBanner, PartnerRail } from "./BenefitCarousels";
import { useCashbackDiscovery } from "./CashbackDiscoveryContext";
import { discoverCashback } from "./cashbackDiscovery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/alloyal/cashback/storefront`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
const labels = { categories: "categorias", highlights: "destaques", recent: "novidades", favorites: "favoritos" };

export default function CashbackStorefront({ onOrganization, onCategory, onPromotions, onError, onNeedsCpf, refresh = 0, onLoadState, initialHome, readOnly = false, renderHome }) {
  const experience=useCashbackDiscovery();
  const enhanced=Boolean(renderHome);
  const [initialSettled, setInitialSettled] = useState(Boolean(initialHome));
  const [data, setData] = useState(initialHome || {});
  const [states, setStates] = useState(() => Object.fromEntries(Object.keys(labels).map((key) => [key, initialHome && Array.isArray(initialHome[key]) ? initialHome.unavailable_sections?.includes(key) ? "failed" : "ready" : "loading"])));
  useEffect(() => {
    const core = [states.highlights, states.categories, states.favorites];
    onLoadState?.(!initialSettled || core.includes("loading") ? "loading" : core.every((value) => value === "ready") ? "ready" : core.includes("ready") ? "partial" : "failed");
  }, [initialSettled, states, onLoadState]);
  const homeRequest = useRef(null);
  const [allCategories, setAllCategories] = useState(false);
  const [cep, setCep] = useState("");
  const [nearby, setNearby] = useState(null);
  const [locationState, setLocationState] = useState("");
  const [locationError, setLocationError] = useState("");
  const callbacks = useRef({ onError, onNeedsCpf });
  const locationRequest = useRef(null);
  callbacks.current = { onError, onNeedsCpf };
  const loadSections = useCallback(async (keys, signal) => {
    setStates((value) => ({ ...value, ...Object.fromEntries(keys.map((key) => [key, "loading"])) }));
    try {
      const { data: result } = await axios.post(API, { section: "home", home_sections: keys }, { ...auth(), signal, timeout: 60000 });
      if (signal.aborted) return [];
      if (result.needs_cpf) { callbacks.current.onNeedsCpf?.(); return []; }
      const failed = keys.filter((key) => result.unavailable_sections?.includes(key) || !Array.isArray(result[key]));
      setData((value) => ({ ...value, ...Object.fromEntries(keys.filter((key) => !failed.includes(key)).map((key) => [key, result[key]])) }));
      setStates((value) => ({ ...value, ...Object.fromEntries(keys.map((key) => [key, failed.includes(key) ? "failed" : "ready"])) }));
      return failed;
    } catch (error) {
      if (signal.aborted) return [];
      setStates((value) => ({ ...value, ...Object.fromEntries(keys.map((key) => [key, "failed"])) }));
      if ([401, 403, 422].includes(error?.response?.status)) callbacks.current.onError?.(error);
      return [401, 403, 422, 503].includes(error?.response?.status) ? [] : keys;
    }
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    homeRequest.current = controller;
    (async () => {
      let failed = [];
      if (refresh !== 0) {
        failed = await loadSections(["favorites"], controller.signal);
      } else if (initialHome) {
        const missing = Object.keys(labels).filter((key) =>
          (!enhanced || key !== "recent") &&
          (!Array.isArray(initialHome[key]) || initialHome.unavailable_sections?.includes(key))
        );
        if (missing.length) failed = await loadSections(missing, controller.signal);
      } else {
        failed = await loadSections(["highlights", "categories", "favorites"], controller.signal);
        if (!controller.signal.aborted) setInitialSettled(true);
        if (!controller.signal.aborted && !enhanced) failed.push(...await loadSections(["recent"], controller.signal));
      }
      // Retry reads only, once, and only the failed sections. Never retry a mutation.
      if (failed.length && !controller.signal.aborted) await loadSections(failed, controller.signal);
      if (!controller.signal.aborted) setInitialSettled(true);
    })();
    return () => controller.abort();
  }, [refresh, loadSections, initialHome, enhanced]);
  const feedback = (key) => states[key] === "loading" && !data[key] ? <div role="status" className="rounded-xl border border-white/10 bg-zinc-900/60 p-5 text-sm text-zinc-400">Carregando {labels[key]}…<div className="mt-3 h-14 rounded-lg bg-white/5 motion-safe:animate-pulse" /></div> : states[key] === "failed" ? <div role="status" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300/20 p-3 text-sm text-amber-200"><p>Não foi possível atualizar {labels[key]}.</p><Button variant="ghost" onClick={() => loadSections([key], homeRequest.current.signal)}>Tentar novamente: {labels[key]}</Button></div> : null;
  useEffect(() => () => locationRequest.current?.abort(), []);
  const locate = () => {
    locationRequest.current?.abort();
    const controller = new AbortController();
    locationRequest.current = controller;
    setLocationError(""); setNearby(null); setLocationState("loading");
    if (!navigator.geolocation) { setLocationState(""); setLocationError("Seu navegador não oferece localização."); return; }
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      if (controller.signal.aborted) return;
      const location = { lat: coords.latitude, lng: coords.longitude };
      try {
        const { data: result } = await axios.post(API, { section: "nearby", ...location }, { ...auth(), signal: controller.signal, timeout: 45000 });
        if (controller.signal.aborted) return;
        if (result.needs_cpf) { callbacks.current.onNeedsCpf?.(); return; }
        setNearby({ items: result.items || [], location, label: "Sua localização atual" });
      } catch (error) { if (!controller.signal.aborted) { setLocationError("Não foi possível consultar as marcas próximas. Tente novamente."); callbacks.current.onError?.(error); } }
      finally { if (!controller.signal.aborted) setLocationState(""); }
    }, (error) => {
      if (controller.signal.aborted) return;
      setLocationState("");
      setLocationError(error.code === 1 ? "Localização não autorizada. Você pode continuar pelas categorias e ofertas online." : "Não foi possível localizar você. Verifique a localização do dispositivo e tente novamente.");
    }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
  };
  const searchCep = async (event) => {
    event.preventDefault();
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) { setLocationError("Informe um CEP com 8 dígitos."); return; }
    locationRequest.current?.abort();
    const controller = new AbortController(); locationRequest.current = controller;
    setLocationError(""); setNearby(null); setLocationState("loading");
    try {
      const { data: result } = await axios.post(API, { section: "nearby", cep: digits }, { ...auth(), signal: controller.signal, timeout: 60000 });
      if (controller.signal.aborted) return;
      if (result.needs_cpf) { callbacks.current.onNeedsCpf?.(); return; }
      setNearby({ items: result.items || [], location: result.location, label: result.location_label });
    } catch (error) {
      if (controller.signal.aborted) return;
      setLocationError(error?.response?.data?.detail || "Não foi possível buscar por CEP. Tente novamente.");
      if ([401, 403].includes(error?.response?.status)) callbacks.current.onError?.(error);
    } finally { if (!controller.signal.aborted) setLocationState(""); }
  };
  const row = (title, items, physical = false) => <PartnerRail title={title} items={items} favorites={experience?.favorites} onFavorite={experience?.toggleFavorite} savingFavorite={experience?.saving} onSelect={(item) => onOrganization(item, physical, nearby?.location)} />;
  const categories = data.categories || [];
  const highlights = data.highlights || [];
  const recommended = discoverCashback([highlights, data.recent || [], data.favorites || []]);
  const banners = highlights.filter((item) => item.banner_image_v2_large || item.banner_image_large || item.banner_image_small);
  const pharmacies = categories.find((item) => item.title?.toLocaleLowerCase("pt-BR") === "farmácias");
  const nearbyContent = <section aria-label="Marcas próximas" className="space-y-3 rounded-2xl border border-white/10 p-5">
      <h2 className="font-nunito text-2xl font-bold">Marcas próximas</h2>
      <p className="text-sm text-zinc-400">Informe um CEP ou use sua localização atual para encontrar lojas próximas. As coordenadas da região escolhida serão enviadas à Alloyal para esta consulta.</p>
      <form onSubmit={searchCep} className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-48"><label htmlFor="cashback-nearby-cep" className="mb-1 block text-sm text-zinc-300">Buscar por CEP</label><Input id="cashback-nearby-cep" inputMode="numeric" autoComplete="postal-code" placeholder="00000-000" maxLength={9} value={cep} onChange={(event) => { const digits = event.target.value.replace(/\D/g, "").slice(0, 8); setCep(digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits); }} className="border-white/20 bg-zinc-900 text-white" /></div>
        <Button type="submit" disabled={locationState === "loading"}>Buscar por CEP</Button>
      </form>
      <Button onClick={locate} disabled={locationState === "loading"}>{locationState === "loading" ? "Buscando marcas próximas..." : "Usar minha localização"}</Button>
      {locationError && <p role="alert" className="text-sm text-amber-300">{locationError}</p>}
      {nearby?.label && <p role="status" className="text-sm cashback-accent-text">Localização aproximada da busca: {nearby.label}</p>}
      {nearby && (nearby.items.length ? row("Perto de você", nearby.items, true) : <p>Nenhuma marca próxima encontrada.</p>)}
      {pharmacies && <Button variant="outline" className="border-zinc-600 bg-transparent" onClick={() => onCategory(pharmacies, nearby?.location)}>Explorar farmácias</Button>}
    </section>;
  if(renderHome)return renderHome({home:data,feedback,nearbyContent});
  return <div className="space-y-8" data-testid="cashback-storefront">
    <BenefitBanner items={banners} onSelect={onOrganization} />
    {feedback("highlights")}
    {recommended.length > 0 && <section aria-label="Descobrir cashback" className="space-y-3 rounded-2xl border border-violet-300/20 p-4">
      {row("Maiores percentuais desta seleção", recommended)}
      <p className="text-xs text-zinc-400">Ordenados pelo percentual anunciado entre os destaques, novidades e favoritos carregados. Não representa todo o catálogo. Ofertas em reais e descontos não entram nesta comparação; valores “até” dependem do produto e das condições.</p>
    </section>}
    <section aria-label="Categorias" className="space-y-3">
      <div className="flex items-center justify-between gap-3"><h2 className="font-nunito text-2xl font-bold">Categorias</h2><Button variant="ghost" aria-expanded={allCategories} onClick={() => setAllCategories((value) => !value)}>{allCategories ? "Ver menos categorias" : "Ver todas as categorias"}</Button></div>
      {feedback("categories")}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{(allCategories ? categories : categories.slice(0, 8)).map((category) => <button key={category.id} onClick={() => onCategory(category)} className="relative min-h-24 overflow-hidden rounded-xl border border-white/10 bg-zinc-900 p-4 text-left hover:border-violet-300">
        {(category.image_v2 || category.image) && <img src={category.image_v2 || category.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-35" />}
        <span className="relative font-bold">{category.title}</span>
      </button>)}</div>
      <Button onClick={onPromotions}>Ver promoções</Button>
    </section>
    {highlights.length > 0 && row("Parceiros em destaque", highlights)}
    {!readOnly && nearbyContent}
    {feedback("recent")}
    {(data.recent || []).length > 0 && row("Novidades", data.recent)}
    {!readOnly && feedback("favorites")}
    {!readOnly && ((data.favorites || []).length ? row("Parceiros favoritos", data.favorites) : states.favorites === "ready" ? <section><h2 className="font-nunito text-2xl font-bold">Parceiros favoritos</h2><p className="mt-2 text-sm text-zinc-400">Abra uma marca e toque em Favoritar para encontrá-la aqui.</p></section> : null)}
  </div>;
}
